import Dexie, { type Table } from 'dexie';
import type { SyncTable, Uuid } from './types';

/**
 * The local store. Two jobs:
 *   1. the outbox, which is the only path a write ever takes, and
 *   2. read caches, so every screen a person has opened still opens with the
 *      network off.
 *
 * Budget, from Section 11: ops precache under 150 MB, guest under 60 MB.
 * Photos are the only thing here that is large, and they leave as soon as they
 * upload.
 */

/**
 * `dead` is the parking bay (defect D5). An op that the island refuses for a
 * reason no amount of waiting will change (a column that does not exist, a
 * policy that says no) used to sit at the head of the queue and hold every
 * later tick behind it, forever, behind a badge that only said "1 sin enviar".
 * After a handful of attempts it moves here instead: the queue carries on, and
 * the person is shown it with a retry and a discard.
 */
export type OutboxStatus = 'pending' | 'sending' | 'failed' | 'dead';

export type OutboxOp = {
  /**
   * The client generated UUID. It is the primary key here and the value of
   * client_id on the server row, which is what makes a retry a no-op rather
   * than a duplicate.
   */
  id: Uuid;
  table: SyncTable;
  kind: 'insert' | 'update';
  payload: Record<string, unknown>;
  /** For an update: the row to match, normally { client_id } or { id }. */
  match?: Record<string, unknown>;
  /** Monotonic within a device. The drain runs in this order, oldest first. */
  seq: number;
  createdAt: number;
  attempts: number;
  nextAttemptAt: number;
  status: OutboxStatus;
  lastError?: string;
  /** When it was parked. Only set on a `dead` op. */
  deadAt?: number;
};

export type PhotoStatus = 'pending' | 'uploading' | 'done' | 'failed';

export type OutboxPhoto = {
  /** Also the file name stem, so the path is known before the upload starts. */
  id: Uuid;
  bucket: string;
  /** stay_id/run_id/item_id-timestamp.jpg */
  path: string;
  blob: Blob;
  bytes: number;
  createdAt: number;
  attempts: number;
  nextAttemptAt: number;
  status: PhotoStatus;
  lastError?: string;
  /**
   * When this upload was handed to the network. A row still holding `uploading`
   * with nothing in flight is a photo whose app was killed mid upload (defect
   * D3), and this is how the next start tells that apart from one that is
   * genuinely on its way.
   */
  startedAt?: number;
};

/** A cached server row, keyed by entity and id. One table, many entities. */
export type CacheRow = {
  key: string;
  entity: string;
  id: string;
  row: unknown;
  cachedAt: number;
};

export type MetaRow = { key: string; value: unknown };

export class DiaDexie extends Dexie {
  outbox!: Table<OutboxOp, string>;
  photos!: Table<OutboxPhoto, string>;
  cache!: Table<CacheRow, string>;
  meta!: Table<MetaRow, string>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      // seq is indexed because the drain is strictly in order.
      outbox: 'id, seq, status, nextAttemptAt, table',
      photos: 'id, status, nextAttemptAt',
      cache: 'key, entity, [entity+id], cachedAt',
      meta: 'key',
    });
  }
}

let db: DiaDexie | null = null;

/**
 * The single local database for this shell. The guest app and the ops app run
 * on different origins, so the names only matter for clarity.
 */
export function getDb(name = 'dia-bonito'): DiaDexie {
  if (!db) db = new DiaDexie(name);
  return db;
}

/** Tests only: drop the singleton so the next getDb starts clean. */
export async function resetDb(): Promise<void> {
  if (db) {
    await db.delete();
    db.close();
    db = null;
  }
}

export function cacheKey(entity: string, id: string): string {
  return `${entity}:${id}`;
}
